/**
 * A tela de Configuração (história 11.2 — decisão 12 de 23/09/2026; antes, "Administração").
 *
 *     /configuracao                  aba Negócios: régua, motivos de perda, Previsão de fechamento,
 *                                    campos personalizados de Negócio e, para quem tem
 *                                    `gerir_funis_e_etapas`, a Oportunidade automática (13.21a2)
 *     /configuracao?aba=clientes     aba Clientes: origem do cliente, categoria, setor e campos
 *                                    personalizados da Empresa
 *     /configuracao?aba=tarefas      aba Tarefas: os tipos de tarefa
 *     /configuracao?aba=usuarios     aba Usuários (11.1) — o serviço só atende o admin de fábrica
 *     /configuracao?aba=permissoes   aba Permissões (11.1) — idem
 *     /configuracao?aba=metas        aba Metas (11.9): os três números do mês — meta do mês, meta
 *                                    da equipe e a individual de cada vendedor (FR-77)
 *
 * **O caminho antigo, `/administracao`, deixou de existir**, sem redirect: `navegacao.ts` é a fonte
 * única, e um redirect manteria vivo um caminho que a decisão 12 mandou trocar (Design Notes da
 * spec). Quem o tinha decorado cai no fallback do SPA.
 *
 * **O conteúdo foi realocado, e não redesenhado.** Os cartões são os mesmos de antes, com o mesmo
 * comportamento; o que mudou é a casa de cada um. O cartão das listas de domínio foi repartido
 * entre Negócios e Clientes. **A 11.3** tirou Sub setor, renomeou a Origem para "Origem do cliente"
 * (a Origem mora na Empresa; o Negócio herda) e acrescentou os dois catálogos novos: a Previsão de
 * fechamento (dono `negocios`, FR-78) e os campos personalizados de Negócio e da Empresa (FR-79),
 * todos sob a permissão `administrar_listas_previsao_e_campos` — que quem recusa é o serviço. A aba
 * **Metas** não nasceu na 11.2 — "a tela entrega o que existe, e não abas vazias" —; ela chegou
 * com conteúdo na 11.9 (dono `relatorios`, AD-27; permissão `configurar_metas`).
 *
 * **O que saiu da tela.** O cartão "Prazo do ERP" (virou a variável de ambiente
 * `PRAZO_DO_ERP_EM_SEGUNDOS`, AD-6/FR-82 — não há tela); os recebimentos do conector, que moram em
 * Integrações → Carga Máquina; o CRUD de Funis e Etapas, que mora em Funil → Gerenciar funis
 * (`/funil/gerenciar`); e a "Confirmar revisão" da régua (emenda à 9.2, decisão 13).
 *
 * **A prontidão de go-live (9.2) é área fixa acima das abas**, e só para o Admin, como antes: ela
 * audita a configuração inteira, e não uma aba — dentro de uma delas, ela sumiria da vista de quem
 * está configurando outra. O componente devolve `null` para quem não é Admin.
 *
 * **A barra é a mesma para todo mundo, e a rota existe para todos os perfis.** O item de menu só
 * aparece para escopo `todas`, mas quem digitar a URL encontra a tela e é recusado pelo **serviço**.
 * As abas Usuários e Permissões aparecem também ao Gestor: as rotas delas respondem `403` a quem não
 * é admin de fábrica, e os componentes da 11.1 nem chegam a consultar — o painel diz que o acesso
 * é do Admin, em vez de ficar em branco. Esconder é conveniência; quem autoriza é o serviço.
 *
 * **Aba na URL** (EXPERIENCE.md, "Aba na URL"): o search param `aba`, com Negócios como padrão — o
 * `/configuracao` cru abre nela, e um valor desconhecido cai nela também. No celular a faixa rola de
 * lado (`overflow-x`), como o DESIGN.md pede para as abas.
 *
 * **Sem cabeçalho próprio**: o `Cabecalho` do shell desenha o título. Ela é filha de
 * `rotaDaAplicacao` e herda a guarda de sessão.
 */

import { createRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'

import { CamposPersonalizados } from '@/modulos/cadastro/CamposPersonalizados'
import { ListasDeDominio } from '@/modulos/cadastro/ListasDeDominio'
import { camposDaEmpresa } from '@/modulos/cadastro/api'
import { Permissoes } from '@/modulos/identidade/Permissoes'
import { Usuarios } from '@/modulos/identidade/Usuarios'
import { ehAdminDeFabrica } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import '@/modulos/identidade/gestao-de-acesso.css'
import { OportunidadeAutomatica } from '@/modulos/negocios/OportunidadeAutomatica'
import { FormasDePagamento } from '@/modulos/negocios/FormasDePagamento'
import { TetoDeDesconto } from '@/modulos/negocios/TetoDeDesconto'
import { PrevisaoDeFechamento } from '@/modulos/negocios/PrevisaoDeFechamento'
import { ReguaDeDias } from '@/modulos/negocios/ReguaDeDias'
import { TiposDeTarefa } from '@/modulos/negocios/TiposDeTarefa'
import { camposDoNegocio } from '@/modulos/negocios/api'
import { ProntidaoDeGoLive } from '@/modulos/prontidao/ProntidaoDeGoLive'
import { MetasDoMes } from '@/modulos/relatorios/MetasDoMes'
import { useDefinirTitulo } from '@/shell/Shell'
import { CAMINHOS, TITULO_DA_CONFIGURACAO } from '@/shell/navegacao'
import { rotaDaAplicacao } from './aplicacao'

/** As abas da tela, na ordem da barra. */
export type AbaDaConfiguracao =
  'negocios' | 'clientes' | 'tarefas' | 'usuarios' | 'permissoes' | 'metas'

export const ABAS_DA_CONFIGURACAO: readonly { aba: AbaDaConfiguracao; rotulo: string }[] = [
  { aba: 'negocios', rotulo: 'Negócios' },
  { aba: 'clientes', rotulo: 'Clientes' },
  { aba: 'tarefas', rotulo: 'Tarefas' },
  { aba: 'usuarios', rotulo: 'Usuários' },
  { aba: 'permissoes', rotulo: 'Permissões' },
  { aba: 'metas', rotulo: 'Metas' },
]

/**
 * As abas que a barra mostra: todas, menos **Metas** para quem não tem `acessar_relatorios` nem
 * `configurar_metas` — as duas rotas da Meta responderiam `403` (11.9). Esconder é conveniência;
 * quem recusa é o serviço, e a aba digitada na URL cai em Negócios.
 */
export function abasVisiveis(
  permissoes: Readonly<Record<string, string>> | undefined,
): readonly { aba: AbaDaConfiguracao; rotulo: string }[] {
  const veMetas =
    permissoes !== undefined &&
    (permissoes.acessar_relatorios === 'concedida' || permissoes.configurar_metas === 'concedida')
  return ABAS_DA_CONFIGURACAO.filter((item) => item.aba !== 'metas' || veMetas)
}

/** A aba que `/configuracao` cru abre. */
const ABA_PADRAO: AbaDaConfiguracao = 'negocios'

/** O que a URL desta tela carrega. */
export interface BuscaDaConfiguracao {
  aba?: AbaDaConfiguracao
}

/** A frase do painel de Usuários e Permissões para quem não é admin de fábrica. */
export const SEM_ACESSO_A_ABA =
  'Você não tem acesso a esta aba: Usuários e Permissões são administrados pelo Admin.'

/** Só o admin de fábrica é atendido pelas rotas destas duas abas (AD-18, 11.1). */
function SoDoAdmin({ children }: { children: ReactNode }) {
  const { data: sessao } = useSessao()
  if (sessao === undefined || !ehAdminDeFabrica(sessao)) {
    return <p className="configuracao__sem-acesso">{SEM_ACESSO_A_ABA}</p>
  }
  return <>{children}</>
}

function ConteudoDaAba({ aba }: { aba: AbaDaConfiguracao }) {
  switch (aba) {
    case 'negocios':
      return (
        <>
          <ReguaDeDias />
          <ListasDeDominio listas={['motivo_de_perda']} />
          <PrevisaoDeFechamento />
          {/* 13.22b1 (FR-93): o teto de desconto; devolve `null` para a SDR. */}
          <TetoDeDesconto />
          {/* 13.22a2 (FR-92): as formas de pagamento do Orçamento do CRM. */}
          <FormasDePagamento />
          <CamposPersonalizados api={camposDoNegocio} entidade="Negócio" />
          {/* 13.21a2: a regra de cada usuário; devolve `null` sem `gerir_funis_e_etapas`. */}
          <OportunidadeAutomatica />
        </>
      )
    case 'clientes':
      return (
        <>
          <ListasDeDominio listas={['origem_do_cliente', 'categoria', 'setor']} />
          <CamposPersonalizados api={camposDaEmpresa} entidade="Cliente" />
        </>
      )
    case 'tarefas':
      return <TiposDeTarefa />
    case 'usuarios':
      return (
        <SoDoAdmin>
          <Usuarios />
        </SoDoAdmin>
      )
    case 'permissoes':
      return (
        <SoDoAdmin>
          <Permissoes />
        </SoDoAdmin>
      )
    case 'metas':
      return <MetasDoMes />
  }
}

/** O `id` do painel único das abas — o conteúdo troca, o painel é o mesmo. */
const ID_DO_PAINEL = 'configuracao-painel'

/** O `id` do botão de cada aba, apontado pelo `aria-labelledby` do painel. */
function idDaAba(aba: AbaDaConfiguracao): string {
  return `configuracao-aba-${aba}`
}

/**
 * A aba que uma tecla da barra abre, ou `null` quando a tecla não é da barra: setas esquerda e
 * direita dão a volta nas pontas, Home e End vão às pontas (padrão ARIA de abas, ativação
 * automática).
 */
function abaDaTecla(
  tecla: string,
  posicao: number,
  abas: readonly { aba: AbaDaConfiguracao }[],
): AbaDaConfiguracao | null {
  const total = abas.length
  const indice =
    tecla === 'ArrowRight'
      ? (posicao + 1) % total
      : tecla === 'ArrowLeft'
        ? (posicao - 1 + total) % total
        : tecla === 'Home'
          ? 0
          : tecla === 'End'
            ? total - 1
            : null
  return indice === null ? null : (abas[indice]?.aba ?? null)
}

function Configuracao() {
  useDefinirTitulo(TITULO_DA_CONFIGURACAO)
  // A anotação é explícita pela mesma razão das outras telas com busca: `useSearch` da própria
  // rota vê o tipo em construção.
  const { aba }: BuscaDaConfiguracao = rotaDeConfiguracao.useSearch()
  const navegar = rotaDeConfiguracao.useNavigate()
  // Conferida de novo aqui, e não só no `validateSearch`: o roteador preserva a busca crua junto da
  // validada, e um `?aba=inexistente` chegaria até aqui sem esta segunda porta.
  const { data: sessao } = useSessao()
  const abas = abasVisiveis(sessao?.permissoes)
  const aberta: AbaDaConfiguracao = abas.find((item) => item.aba === aba)?.aba ?? ABA_PADRAO

  function abrir(destino: AbaDaConfiguracao) {
    void navegar({ search: destino === ABA_PADRAO ? {} : { aba: destino } })
  }

  return (
    <>
      <ProntidaoDeGoLive />
      <div className="abas-da-configuracao" role="tablist" aria-label="Seções da Configuração">
        {abas.map((item, posicao) => {
          const ativa = item.aba === aberta
          return (
            <button
              key={item.aba}
              id={idDaAba(item.aba)}
              type="button"
              role="tab"
              className="abas-da-configuracao__aba"
              aria-selected={ativa}
              aria-controls={ID_DO_PAINEL}
              // `tabIndex` itinerante (padrão ARIA de abas): só a ativa entra na ordem do Tab; as
              // outras se alcançam pelas setas, que movem o foco e abrem a aba de uma vez.
              tabIndex={ativa ? 0 : -1}
              onClick={() => {
                abrir(item.aba)
              }}
              onKeyDown={(evento) => {
                const destino = abaDaTecla(evento.key, posicao, abas)
                if (destino === null) return
                evento.preventDefault()
                abrir(destino)
                document.getElementById(idDaAba(destino))?.focus()
              }}
            >
              {item.rotulo}
            </button>
          )
        })}
      </div>
      <div role="tabpanel" id={ID_DO_PAINEL} aria-labelledby={idDaAba(aberta)}>
        <ConteudoDaAba aba={aberta} />
      </div>
    </>
  )
}

export const rotaDeConfiguracao = createRoute({
  getParentRoute: () => rotaDaAplicacao,
  path: CAMINHOS.configuracao,
  component: Configuracao,
  /** A URL é entrada: o que não for uma das seis abas cai fora aqui, e a tela abre em Negócios. */
  validateSearch: (parametros: Record<string, unknown>): BuscaDaConfiguracao => {
    const aba = ABAS_DA_CONFIGURACAO.find((item) => item.aba === parametros.aba)?.aba
    return aba === undefined || aba === ABA_PADRAO ? {} : { aba }
  },
})
